import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Select } from '@monigo/ui';
import { useBeneficiaries } from '../beneficiaries/api.js';
import { useCreateTransfer, useQuote, useValidatePromoCode } from './api.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

const CURRENCIES = ['EUR', 'USD', 'CAD'] as const;

type Step = 'beneficiary' | 'review';

function payoutLabel(details: Record<string, unknown>, method: 'mobile_money' | 'bank') {
  if (method === 'bank') return String(details.iban ?? '—');
  return String(details.phone_number ?? '—');
}

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function NextIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  );
}

export function NewTransferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const benefitsQ = useBeneficiaries();
  const quote = useQuote();
  const create = useCreateTransfer();

  const navState = (location.state ?? {}) as { amount?: string; from_currency?: string };
  const initialCurrency = CURRENCIES.includes(navState.from_currency as (typeof CURRENCIES)[number])
    ? (navState.from_currency as (typeof CURRENCIES)[number])
    : 'EUR';
  const initialAmount =
    navState.amount && Number(navState.amount) > 0 ? navState.amount : '100';

  const [step, setStep] = useState<Step>('beneficiary');
  const [beneficiaryId, setBeneficiaryId] = useState<string>('');
  const [from, setFrom] = useState<(typeof CURRENCIES)[number]>(initialCurrency);
  const [amount, setAmount] = useState<string>(initialAmount);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState<string | null>(null);

  const numericAmount = Number(amount);
  const promoValidate = useValidatePromoCode();
  const selectedBeneficiary = benefitsQ.data?.items.find((b) => b.id === beneficiaryId);

  // Idempotency key persists across re-renders for the same compose session.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  useEffect(() => {
    if (numericAmount > 0) {
      quote.mutate({ from_currency: from, to_currency: 'XAF', amount: numericAmount });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, numericAmount]);

  if (benefitsQ.isLoading) return <LoadingScreen />;
  if (benefitsQ.isError) return <ErrorState onRetry={() => benefitsQ.refetch()} />;

  const handleBeneficiarySubmit = (e: FormEvent) => {
    e.preventDefault();
    if (beneficiaryId && quote.data) {
      setStep('review');
    }
  };

  const handleFinalSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!beneficiaryId || !numericAmount) return;
    const res = await create.mutateAsync({
      beneficiary_id: beneficiaryId,
      from_currency: from,
      to_currency: 'XAF',
      amount: numericAmount,
      idempotency_key: idempotencyKey,
      promo_code: promoApplied ?? undefined,
    });
    navigate(`/transfers/${res.transfer.id}`);
  };

  if (step === 'beneficiary') {
    return (
      <div className="mx-auto max-w-xl space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-display font-extrabold text-text">
            {t('transfers.selectBeneficiary')}
          </h1>
        </div>

        <Card className="p-5 md:p-6">
          <form className="flex flex-col gap-4" onSubmit={handleBeneficiarySubmit}>
            <div className="flex flex-col gap-3">
              {benefitsQ.data?.items.map((b) => (
                <label
                  key={b.id}
                  className={`flex items-center gap-3 rounded-[1.5rem] border p-4 transition ${
                    beneficiaryId === b.id
                      ? 'border-primary bg-success/8 shadow-sm'
                      : 'border-border bg-surface hover:border-primary/35'
                  }`}
                >
                  <input
                    type="radio"
                    name="beneficiary"
                    value={b.id}
                    checked={beneficiaryId === b.id}
                    onChange={(e) => setBeneficiaryId(e.target.value)}
                    className="h-4 w-4"
                  />
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/12 font-semibold text-primary">
                    {b.full_name
                      .split(' ')
                      .slice(0, 2)
                      .map((part) => part[0])
                      .join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-text">{b.full_name}</p>
                    <p className="text-sm text-text-muted">
                      {t(`beneficiaries.${b.payout_method === 'bank' ? 'bank' : 'mobileMoney'}`)}
                    </p>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/beneficiaries/new')}
                className="gap-2"
              >
                + {t('beneficiaries.addCta')}
              </Button>
              <Button type="submit" disabled={!beneficiaryId} className="gap-2">
                {t('common.continue')}
                <NextIcon />
              </Button>
            </div>
          </form>
        </Card>

        {/* Amount & currency select inline */}
        <Card className="p-5 md:p-6">
          <h2 className="mb-4 text-lg font-semibold text-text">{t('transfers.amountLabel')}</h2>
          <div className="flex gap-3">
            <Input
              type="number"
              min={1}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100.00"
              className="h-16 text-center text-3xl font-display font-extrabold"
            />
            <Select
              value={from}
              onChange={(v) => setFrom(v as (typeof CURRENCIES)[number])}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              className="h-16 min-w-[6rem]"
            />
          </div>
          {quote.data ? (
            <div className="mt-4 rounded-xl bg-surface-elevated p-4 text-sm">
              <Row label={t('transfers.rate')} value={`1 ${from} = ${quote.data.rate} XAF`} />
              <Row label={t('transfers.fee')} value={`${quote.data.fee} ${from}`} />
              <div className="mt-3 border-t border-border pt-3">
                <p className="text-xs text-text-muted">{t('transfers.theyReceive')}</p>
                <p className="text-2xl font-display font-extrabold text-primary">
                  {quote.data.amount_receive} XAF
                </p>
              </div>
            </div>
          ) : null}
        </Card>
      </div>
    );
  }

  if (step === 'review' && quote.data && selectedBeneficiary) {
    return (
      <div className="mx-auto max-w-xl space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-display font-extrabold text-text">
            {t('transfers.reviewTitle')}
          </h1>
        </div>

        <Card className="p-5 md:p-6">
          <form className="flex flex-col gap-5" onSubmit={handleFinalSubmit}>
            <Card className="border-success/20 bg-success/8 p-5">
              <p className="text-sm text-text-muted">{t('transfers.theyReceive')}</p>
              <p className="amount mt-1 text-4xl font-display font-extrabold text-primary">
                {quote.data.amount_receive} XAF
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <Row label={t('transfers.youSend')} value={`${quote.data.amount_send} ${from}`} />
                <Row label={t('transfers.fee')} value={`${quote.data.fee} ${from}`} />
                <Row label={t('transfers.rate')} value={`1 ${from} = ${quote.data.rate} XAF`} />
              </div>
              {quote.data.has_dynamic_tiers ? (
                <p className="mt-2 text-xs text-primary/70">ⓘ {t('landing.estimateHintTiered')}</p>
              ) : null}
            </Card>

            <Card className="p-4">
              <p className="text-xs uppercase tracking-[0.14em] text-text-muted">{t('transfers.beneficiary')}</p>
              <p className="mt-2 text-lg font-semibold text-text">
                {selectedBeneficiary.full_name}
              </p>
              <p className="mt-1 text-sm text-text-muted">
                {payoutLabel(selectedBeneficiary.payout_details, selectedBeneficiary.payout_method)}
              </p>
            </Card>

            {/* Promo code */}
            <div className="flex items-center gap-3">
              <Input
                placeholder={t('transfers.promoCodePlaceholder')}
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1"
                disabled={!!promoApplied}
              />
              {promoApplied ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPromoApplied(null);
                    setPromoCode('');
                  }}
                >
                  {t('common.clear')}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  isLoading={promoValidate.isPending}
                  disabled={!promoCode.trim()}
                  onClick={() => {
                    promoValidate.mutate(
                      {
                        code: promoCode,
                        from_currency: from,
                        amount_send: numericAmount,
                        fee: quote.data?.fee ?? 0,
                      },
                      {
                        onSuccess: (result) => {
                          if (result.valid) {
                            setPromoApplied(promoCode);
                          }
                        },
                      },
                    );
                  }}
                >
                  {t('transfers.applyCode')}
                </Button>
              )}
            </div>

            {promoValidate.data && !promoValidate.data.valid && promoCode && !promoApplied ? (
              <p className="text-xs text-danger">{t('transfers.promoCodeInvalid')}</p>
            ) : null}

            {promoApplied && promoValidate.data?.valid ? (
              <Card className="border-success/20 bg-success/8 p-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-success">
                  ✓ {promoValidate.data.name}
                </p>
                <p className="text-xs text-text-muted">
                  {t('transfers.promoDiscount', { amount: `${promoValidate.data.discount_applied} ${from}` })}
                </p>
              </Card>
            ) : null}

            {create.isError ? (
              <div className="rounded-[1.25rem] bg-danger/10 p-3 text-sm text-danger">
                {t('errors.generic')}
              </div>
            ) : null}

            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep('beneficiary')}
                className="flex-1 gap-2"
              >
                <BackIcon />
                {t('common.back')}
              </Button>
              <Button type="submit" isLoading={create.isPending} className="flex-1 gap-2">
                {t('transfers.submitCta')}
                <NextIcon />
              </Button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  return null;
}

function Row({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-text-muted">{label}</span>
      <span className={emphasis ? 'font-display text-lg font-semibold text-success' : 'text-text'}>
        {value}
      </span>
    </div>
  );
}
